class FacetSlider extends HTMLElement {
    #abortController;
  
    connectedCallback() {
        this.#abortController = new AbortController();
    
        const scriptId = this.dataset.jsonId;
        const scriptEl = document.getElementById(scriptId);
        if (!scriptEl) return;
    
        const json = JSON.parse(scriptEl.textContent);
    
        const data = {
          param_name: json.param_name,
          title: this.dataset.title,
          min: json.min,
          max: json.max,
          values: json.values,
          activeMin: this._computeActiveMin(json),
          activeMax: this._computeActiveMax(json),
          filterName: this.dataset.filterName
        };
    
        this._render(data);
        this._initEvents(data);
      }
  
    disconnectedCallback() {
      this.#abortController.abort();
    }

    _computeActiveMin(json) {
        const active = json.values.filter(v => v.active).map(v => v.value);
        if (active.length === 0) return json.min;
        return Math.min(...active);
    }

    _computeActiveMax(json) {
        const active = json.values.filter(v => v.active).map(v => v.value);
        if (active.length === 0) return json.max;
        return Math.max(...active);
    }
  
    _parseData() {
      return {
        title: this.dataset.title,
        min: parseInt(this.dataset.min),
        max: parseInt(this.dataset.max),
        activeMin: parseInt(this.dataset.activeMin),
        activeMax: parseInt(this.dataset.activeMax),
        values: JSON.parse(this.dataset.values),
        filterName: this.dataset.filterName
      };
    }
  
    _render({ min, max, activeMin, activeMax }) {
      this.innerHTML = `
        <div class="range-group"
         style="--range-min:${((activeMin - min) / (max - min)) * 100}%;
                --range-max:${((activeMax - min) / (max - min)) * 100}%;">
        <input type="range" class="range range-min" min="${min}" max="${max}" value="${activeMin}">
        <input type="range" class="range range-max" min="${min}" max="${max}" value="${activeMax}">
        </div>

        <div class="facet-slider__input-group custom-range__input-group">
        <label class="input-prefix text-sm">
            <input
            aria-label="${this.dataset.title} minimum"
            class="field facet-input-min"
            type="number"
            inputmode="numeric"
            min="${min}"
            max="${max}"
            value="${activeMin}"
            autocomplete="off"
            >
        </label>

        <span class="subheading">to</span>

        <label class="input-prefix text-sm">
            <input
            aria-label="${this.dataset.title} maximum"
            class="field facet-input-max"
            type="number"
            inputmode="numeric"
            min="${min}"
            max="${max}"
            value="${activeMax}"
            autocomplete="off"
            >
        </label>
        </div>
      `;
    }
  
    _initEvents({ min, max, values, filterName }) {
      const group = this.querySelector(".range-group");
      const minInput = this.querySelector(".range-min");
      const maxInput = this.querySelector(".range-max");
  
      const checkboxSelector = `input[type="checkbox"][name="${filterName}"]`;
      const checkboxes = [...document.querySelectorAll(checkboxSelector)];
  
      const numMin = this.querySelector(".facet-input-min");
      const numMax = this.querySelector(".facet-input-max");

      const snapToValue = (raw) => {
        let closest = values[0].value;
        let smallestDiff = Math.abs(raw - closest);
        for (const v of values) {
          const diff = Math.abs(raw - v.value);
          if (diff < smallestDiff) {
            smallestDiff = diff;
            closest = v.value;
          }
        }
        return closest;
      };
  
      const updateCheckboxes = (low, high) => {
        checkboxes.forEach(cb => {
          const val = parseInt(cb.value);
          cb.checked = val >= low && val <= high;
        });
      };
  
      const fireShopifyEvent = () => {

        const form = document.querySelector("facet-filters-form form");
        if (form) {
          form.dispatchEvent(new Event("input", { bubbles: true }));
        }
      };
  
      minInput.addEventListener("input", (e) => {
        let raw = parseInt(e.target.value);
        raw = Math.min(raw, parseInt(maxInput.value) - 1);
        const snapped = snapToValue(raw);
        numMin.value = raw;
        e.target.value = snapped;
        const percent = ((snapped - min) / (max - min)) * 100;
        group.style.setProperty("--range-min", `${percent}%`);
  
        updateCheckboxes(snapped, parseInt(maxInput.value));
      }, { signal: this.#abortController.signal });
  
      maxInput.addEventListener("input", (e) => {
        let raw = parseInt(e.target.value);
        raw = Math.max(raw, parseInt(minInput.value) + 1);
        const snapped = snapToValue(raw);
        numMax.value = raw;
        e.target.value = snapped;
        const percent = ((snapped - min) / (max - min)) * 100;
        group.style.setProperty("--range-max", `${percent}%`);
  
        updateCheckboxes(parseInt(minInput.value), snapped);
      }, { signal: this.#abortController.signal });
      
      numMin.addEventListener("change", (e) => {
        let v = parseInt(e.target.value);
      
        // clamp
        v = Math.max(min, v);
        v = Math.min(v, parseInt(numMax.value) - 1);
      
        const snapped = snapToValue(v);
      
        // update slider
        minInput.value = snapped;
        const pct = ((snapped - min) / (max - min)) * 100;
        group.style.setProperty("--range-min", `${pct}%`);
      
        // update numeric
        numMin.value = snapped;
      
        updateCheckboxes(snapped, parseInt(maxInput.value));
        fireShopifyEvent();
      }, { signal: this.#abortController.signal });

      numMax.addEventListener("change", (e) => {
        let v = parseInt(e.target.value);
      
        // clamp
        v = Math.min(max, v);
        v = Math.max(v, parseInt(numMin.value) + 1);
      
        const snapped = snapToValue(v);
      
        // update slider
        maxInput.value = snapped;
        const pct = ((snapped - min) / (max - min)) * 100;
        group.style.setProperty("--range-max", `${pct}%`);
      
        // update numeric
        numMax.value = snapped;
      
        updateCheckboxes(parseInt(minInput.value), snapped);
        fireShopifyEvent();
      }, { signal: this.#abortController.signal });

      minInput.addEventListener("change", fireShopifyEvent, { signal: this.#abortController.signal });
      maxInput.addEventListener("change", fireShopifyEvent, { signal: this.#abortController.signal });
    }
  }
  
  if (!customElements.get("facet-slider")) {
    customElements.define("facet-slider", FacetSlider);
  }